import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
  SHORT_MONTHS,
} from "@shared-core/domain/formatters";
import { useCanManageTargets, useCanReadTargets } from "../app/auth/hooks";
import { Card } from "../app/components/primitives/card";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { Lock, Save, Sparkles, Trash2, Check, History } from "lucide-react";
import { WorkspaceNav } from "../app/components/domain/forecasting/WorkspaceNav";
import { currentTime } from "@shared-core/domain/current-time";

const MEASURE_LABEL: Record<API.TargetMeasure, string> = {
  revenue: "Sales value",
  contribution: "Contribution",
};

const CHANGE_SCOPE_LABEL: Record<API.TargetPlanChange["scope"], string> = {
  cell: "Monthly target",
  rep_fy: "Rep FY total",
  company_month: "Company month total",
  company_fy: "Company FY total",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtK(n: number | null | undefined): string {
  const v = n ?? 0;
  if (v === 0) return "··";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs < 1000) return `${sign}£${Math.round(abs)}`;
  return `${sign}£${Math.round(abs / 1000)}k`;
}

function fmtFy(n: number | null | undefined): string {
  const v = n ?? 0;
  if (v === 0) return "—";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${sign}£${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1000) return `${sign}£${Math.round(abs / 1000)}k`;
  return `${sign}£${Math.round(abs)}`;
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[£,\s]/g, "").toLowerCase();
  if (cleaned === "") return 0;
  let multiplier = 1;
  let body = cleaned;
  if (cleaned.endsWith("m")) {
    multiplier = 1_000_000;
    body = cleaned.slice(0, -1);
  } else if (cleaned.endsWith("k")) {
    multiplier = 1000;
    body = cleaned.slice(0, -1);
  }
  const n = parseFloat(body);
  if (!Number.isFinite(n)) return null;
  const v = Math.round(n * multiplier);
  if (v < 0 || v > 100_000_000) return null;
  return v;
}

function monthLabel(_year: number, month: number): string {
  return SHORT_MONTHS[month - 1] ?? "";
}

function currentFiscalYear(today = currentTime()): number {
  // UK FY starts April. FY26 = Apr 2026 → Mar 2027.
  const m = today.getMonth() + 1;
  const y = today.getFullYear();
  return m >= 4 ? y : y - 1;
}

// ---------------------------------------------------------------------------
// Editable cell
// ---------------------------------------------------------------------------

interface TargetCellProps {
  value: number;
  ghost?: number;
  locked?: boolean;
  readOnly?: boolean;
  onCommit: (v: number) => void;
}

function TargetCell({
  value,
  ghost,
  locked,
  readOnly,
  onCommit,
}: TargetCellProps) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState(
    value > 0 ? `${Math.round(value / 1000)}` : "",
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!editing) {
      setRaw(value > 0 ? `${Math.round(value / 1000)}` : "");
    }
  }, [value, editing]);

  if (locked) {
    return (
      <div className="text-center px-1 py-1 tabular-nums text-muted-foreground/60 bg-muted/30">
        <Lock className="w-3 h-3 inline-block mr-0.5" />
        {fmtK(value)}
      </div>
    );
  }

  const ghostStr = ghost !== undefined && ghost !== 0 ? fmtK(ghost) : "";

  return (
    <div className="px-0.5 py-0.5">
      <Input
        value={raw}
        readOnly={readOnly}
        onFocus={() => setEditing(true)}
        onChange={(e) => {
          setRaw(e.target.value);
          setError(false);
        }}
        onBlur={() => {
          setEditing(false);
          const parsed = parseAmount(raw);
          if (parsed === null) {
            setError(true);
            return;
          }
          // Inputs are entered as £k for compact editing.
          const next =
            raw.toLowerCase().includes("k") || raw.includes("£")
              ? parsed
              : parsed * 1000;
          if (next !== value) onCommit(next);
        }}
        className={`h-7 text-right text-sm tabular-nums px-1 ${
          error ? "border-red-500 bg-red-50" : ""
        } ${readOnly ? "cursor-not-allowed opacity-60" : ""}`}
        placeholder={ghostStr || "·"}
        aria-invalid={error}
      />
      <div className="text-center text-[10px] text-muted-foreground/60 tabular-nums leading-tight">
        {ghostStr || "·"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TargetPlanningPage() {
  const adapter = useDataAdapter();
  const canManage = useCanManageTargets();
  const canRead = useCanReadTargets();

  const [fiscalYear, setFiscalYear] = useState<number>(() =>
    currentFiscalYear(),
  );
  const [plans, setPlans] = useState<API.TargetPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [grid, setGrid] = useState<API.TargetPlanGrid | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
  const [stretchPct, setStretchPct] = useState<string>("0");
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishNotes, setPublishNotes] = useState("");
  // The measure shown and edited. Sales value is what the loaded plans hold;
  // contribution is the second series managers set alongside it.
  const [measure, setMeasure] = useState<API.TargetMeasure>("revenue");
  // Every edit is logged with a reason; the note is required before a cell
  // will save, so a whole editing session can share one note.
  const [changeNote, setChangeNote] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [changes, setChanges] = useState<API.TargetPlanChange[]>([]);

  const savedTimerRef = useRef<number | null>(null);

  // Load plans for the chosen FY. We keep `loading=true` here even after
  // the list resolves — if we pick a selectedPlanId, the grid effect
  // takes over and only flips loading off when the grid actually arrives.
  // That avoids a "No plan loaded" flash between the two resolutions.
  // Only when there's nothing to select do we clear loading from here.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adapter
      .listTargetPlans(fiscalYear)
      .then((res) => {
        if (cancelled) return;
        setPlans(res.plans);
        const draft = res.plans.find((p) => p.status === "draft");
        const published = res.plans.find((p) => p.status === "published");
        const next = draft?.id ?? published?.id ?? null;
        setSelectedPlanId(next);
        if (next === null) setLoading(false);
      })
      .catch((e) => {
        console.error("Failed to load target plans", e);
        if (!cancelled) {
          setPlans([]);
          setSelectedPlanId(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, fiscalYear]);

  // Load grid for the selected plan. Loading stays true until grid
  // arrives (or until we know there's no plan to load) so the "No plan
  // loaded" empty state doesn't flash between plans-load and grid-load.
  useEffect(() => {
    let cancelled = false;
    if (!selectedPlanId) {
      setGrid(null);
      return;
    }
    setLoading(true);
    adapter
      .getTargetPlanGrid(selectedPlanId, undefined, { currency: measure })
      .then((g) => {
        if (!cancelled) setGrid(g);
      })
      .catch((e) => {
        console.error("Failed to load plan grid", e);
        if (!cancelled) setGrid(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, selectedPlanId, measure]);

  const refreshChanges = useCallback(async () => {
    if (!selectedPlanId) return;
    try {
      const res = await adapter.getTargetPlanChanges(selectedPlanId);
      setChanges(res.changes);
    } catch (e) {
      console.error("Failed to load target plan changes", e);
    }
  }, [adapter, selectedPlanId]);

  useEffect(() => {
    if (historyOpen) void refreshChanges();
  }, [historyOpen, refreshChanges]);

  const flashSaved = useCallback(() => {
    setSaving("saved");
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(
      () => setSaving("idle"),
      1200,
    ) as unknown as number;
    if (historyOpen) void refreshChanges();
  }, [historyOpen, refreshChanges]);

  const note = changeNote.trim();
  const canEdit = canManage && note.length > 0;

  // ── Mutations (optimistic + autosave) ────────────────────────────────────

  const updateCell = useCallback(
    async (userId: string, year: number, month: number, value: number) => {
      if (!selectedPlanId || !grid || !canEdit) return;
      // Optimistic
      setGrid((g) => {
        if (!g) return g;
        const monthIdx = g.months.findIndex(
          (m) => m.year === year && m.month === month,
        );
        if (monthIdx < 0) return g;
        return {
          ...g,
          reps: g.reps.map((r) =>
            r.user_id === userId
              ? {
                  ...r,
                  monthly_targets: r.monthly_targets.map((t, i) =>
                    i === monthIdx ? value : t,
                  ),
                }
              : r,
          ),
        };
      });
      setSaving("saving");
      try {
        await adapter.updateTargetCells(selectedPlanId, {
          measure,
          cells: [{ user_id: userId, year, month, target_amt: value }],
          note,
        });
        flashSaved();
      } catch (e) {
        console.error("Failed to update cell", e);
        setSaving("error");
      }
    },
    [adapter, selectedPlanId, grid, canEdit, measure, note, flashSaved],
  );

  const updateRepFy = useCallback(
    async (userId: string, value: number) => {
      if (!selectedPlanId || !grid || !canEdit) return;
      setGrid((g) => {
        if (!g) return g;
        return {
          ...g,
          reps: g.reps.map((r) =>
            r.user_id === userId
              ? {
                  ...r,
                  fy_target: value,
                  fy_target_gap:
                    value - r.monthly_targets.reduce((s, t) => s + t, 0),
                }
              : r,
          ),
        };
      });
      setSaving("saving");
      try {
        await adapter.updateTargetRepFy(selectedPlanId, {
          measure,
          user_id: userId,
          target_amt: value,
          note,
        });
        flashSaved();
      } catch (e) {
        console.error("Failed to update rep FY target", e);
        setSaving("error");
      }
    },
    [adapter, selectedPlanId, grid, canEdit, measure, note, flashSaved],
  );

  const updateCompanyMonth = useCallback(
    async (year: number, month: number, value: number) => {
      if (!selectedPlanId || !grid || !canEdit) return;
      setGrid((g) => {
        if (!g) return g;
        const idx = g.months.findIndex(
          (m) => m.year === year && m.month === month,
        );
        if (idx < 0) return g;
        return {
          ...g,
          company: {
            ...g.company,
            monthly_targets: g.company.monthly_targets.map((t, i) =>
              i === idx ? value : t,
            ),
          },
        };
      });
      setSaving("saving");
      try {
        await adapter.updateTargetCompanyMonth(selectedPlanId, {
          measure,
          year,
          month,
          target_amt: value,
          note,
        });
        flashSaved();
      } catch (e) {
        console.error("Failed to update company-month target", e);
        setSaving("error");
      }
    },
    [adapter, selectedPlanId, grid, canEdit, measure, note, flashSaved],
  );

  const updateCompanyFy = useCallback(
    async (value: number) => {
      if (!selectedPlanId || !grid || !canEdit) return;
      setGrid((g) =>
        g ? { ...g, company: { ...g.company, fy_target: value } } : g,
      );
      setSaving("saving");
      try {
        await adapter.updateTargetCompanyFy(selectedPlanId, {
          measure,
          target_amt: value,
          note,
        });
        flashSaved();
      } catch (e) {
        console.error("Failed to update company FY target", e);
        setSaving("error");
      }
    },
    [adapter, selectedPlanId, grid, canEdit, measure, note, flashSaved],
  );

  const updateNewBizTarget = useCallback(
    async (userId: string, year: number, month: number, value: number) => {
      if (!selectedPlanId || !grid || !canManage) return;
      setGrid((g) => {
        if (!g) return g;
        const idx = g.months.findIndex(
          (m) => m.year === year && m.month === month,
        );
        if (idx < 0) return g;
        return {
          ...g,
          reps: g.reps.map((r) =>
            r.user_id === userId
              ? {
                  ...r,
                  monthly_new_business_target:
                    r.monthly_new_business_target.map((t, i) =>
                      i === idx ? value : t,
                    ),
                }
              : r,
          ),
        };
      });
      setSaving("saving");
      try {
        await adapter.setNewBusinessTarget(selectedPlanId, {
          user_id: userId,
          year,
          month,
          amount: value || null,
        });
        flashSaved();
      } catch (e) {
        console.error("Failed to update new-business target", e);
        setSaving("error");
      }
    },
    [adapter, selectedPlanId, grid, canManage, flashSaved],
  );

  // ── Plan lifecycle ───────────────────────────────────────────────────────

  const createDraft = useCallback(
    async (copyFromPlanId?: string) => {
      try {
        const draft = await adapter.createDraftTargetPlan({
          fiscal_year: fiscalYear,
          copy_from_plan_id: copyFromPlanId,
        });
        setPlans((p) => [draft, ...p]);
        setSelectedPlanId(draft.id);
      } catch (e) {
        console.error("Failed to create draft", e);
        setSaving("error");
      }
    },
    [adapter, fiscalYear],
  );

  // Seed flow is two-step: clicking "Apply" opens an inline confirm card
  // (no window.confirm() — that blocks the Chrome event loop and was
  // freezing the tab in testing). The card states honestly what gets
  // overwritten: every monthly cell AND every manual FY-rep override,
  // because the backend seed writes both.
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);
  const requestSeed = useCallback(() => {
    if (!selectedPlanId) return;
    const pct = parseFloat(stretchPct);
    if (!Number.isFinite(pct) || pct < -50 || pct > 200) {
      setSaving("error");
      return;
    }
    setSeedConfirmOpen(true);
  }, [selectedPlanId, stretchPct]);
  const seedFromForecast = useCallback(async () => {
    if (!selectedPlanId) return;
    const pct = parseFloat(stretchPct);
    if (!Number.isFinite(pct)) return;
    setSeedConfirmOpen(false);
    setSaving("saving");
    try {
      await adapter.seedTargetPlanFromForecast(selectedPlanId, {
        measure,
        stretch_pct: pct,
      });
      const fresh = await adapter.getTargetPlanGrid(selectedPlanId, undefined, {
        currency: measure,
      });
      setGrid(fresh);
      flashSaved();
    } catch (e) {
      console.error("Seed failed", e);
      setSaving("error");
    }
  }, [adapter, selectedPlanId, stretchPct, measure, flashSaved]);

  const publish = useCallback(async () => {
    if (!selectedPlanId) return;
    setSaving("saving");
    try {
      const published = await adapter.publishTargetPlan(selectedPlanId, {
        notes: publishNotes,
      });
      setPlans((p) => p.map((x) => (x.id === published.id ? published : x)));
      flashSaved();
      setPublishOpen(false);
      setPublishNotes("");
    } catch (e) {
      console.error("Publish failed", e);
      setSaving("error");
    }
  }, [adapter, selectedPlanId, publishNotes, flashSaved]);

  // Two-step delete: clicking "Delete draft" opens an inline confirm card
  // (no window.confirm() — that blocks the Chrome event loop and was
  // freezing the tab on the seed flow; same hazard here). The card is the
  // confirmation step. Same pattern as seedConfirm and the cycle open/close
  // forms on PlanPage.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const requestDeleteDraft = useCallback(() => {
    if (!selectedPlanId) return;
    setDeleteConfirmOpen(true);
  }, [selectedPlanId]);
  const deleteDraft = useCallback(async () => {
    if (!selectedPlanId) return;
    setDeleteConfirmOpen(false);
    try {
      await adapter.deleteTargetDraft(selectedPlanId);
      const remaining = plans.filter((p) => p.id !== selectedPlanId);
      setPlans(remaining);
      const fallback = remaining.find((p) => p.status === "published");
      setSelectedPlanId(fallback?.id ?? null);
    } catch (e) {
      console.error("Delete failed", e);
      setSaving("error");
    }
  }, [adapter, selectedPlanId, plans]);

  // Declared before the manager-gate early-return: React requires the same
  // hook order on every render, so all hooks must precede conditional returns.
  const fyOptions = useMemo(() => {
    const cur = currentFiscalYear();
    return [cur, cur + 1, cur + 2];
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────
  // Sales reps see the published plan read-only (drafts never reach them: the
  // API filters them out); managers see and edit drafts too. Other roles hold
  // no targets permission, so say so rather than render an empty grid over a
  // 403.

  if (!canRead) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card className="p-8 text-center">
          <h1 className="text-xl font-semibold mb-2">No access to targets</h1>
          <p className="text-muted-foreground">
            Targets are visible to managers and sales reps.
          </p>
        </Card>
      </div>
    );
  }

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;
  const isDraft = selectedPlan?.status === "draft";
  const isPublished = selectedPlan?.status === "published";
  const visibleChanges = changes.filter((c) => c.measure === measure);
  return (
    <div className="max-w-[1600px] mx-auto px-4 py-6 space-y-4">
      <WorkspaceNav active="targets" />
      {/* Header */}
      <Card className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold">Targets</h1>
              <Select
                value={String(fiscalYear)}
                onValueChange={(v) => setFiscalYear(parseInt(v, 10))}
              >
                <SelectTrigger className="h-8 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fyOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      FY{String(y).slice(-2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPlan && (
              <div className="flex items-center gap-3 text-sm">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    isDraft
                      ? "bg-amber-100 text-amber-900"
                      : "bg-emerald-100 text-emerald-900"
                  }`}
                >
                  {isDraft ? "Draft" : "Published"} v{selectedPlan.version}
                </span>
                <span className="text-muted-foreground">
                  Created {formatDateOrDash(selectedPlan.created_at)}
                </span>
                {plans.length > 1 && (
                  <Select
                    value={selectedPlanId ?? undefined}
                    onValueChange={setSelectedPlanId}
                  >
                    <SelectTrigger className="h-7 w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          v{p.version} · {p.status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <SaveIndicator state={saving} />
            <MeasureToggle value={measure} onChange={setMeasure} />
            {selectedPlan && (
              <Button
                variant={historyOpen ? "secondary" : "outline"}
                size="sm"
                onClick={() => setHistoryOpen((v) => !v)}
                aria-pressed={historyOpen}
              >
                <History className="w-4 h-4 mr-1" />
                History
              </Button>
            )}
            {isDraft && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={requestDeleteDraft}
                  className="text-red-700 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete draft
                </Button>
                <Button size="sm" onClick={() => setPublishOpen((v) => !v)}>
                  <Check className="w-4 h-4 mr-1" />
                  Publish
                </Button>
              </>
            )}
            {isPublished && canManage && (
              <Button
                size="sm"
                onClick={() => createDraft(selectedPlanId ?? undefined)}
              >
                <Save className="w-4 h-4 mr-1" />
                Create new draft from this
              </Button>
            )}
            {!selectedPlan && !loading && canManage && (
              <Button size="sm" onClick={() => createDraft()}>
                <Save className="w-4 h-4 mr-1" />
                Create FY{String(fiscalYear).slice(-2)} draft
              </Button>
            )}
          </div>
        </div>

        {/* Change note: required before any edit saves */}
        {isDraft && (
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-3 text-sm">
            <label
              className="text-muted-foreground whitespace-nowrap"
              htmlFor="change-note"
            >
              Change note
            </label>
            <Input
              id="change-note"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="Why these figures are changing, e.g. board-approved uplift"
              maxLength={500}
              className="h-8 max-w-xl"
              aria-invalid={!canEdit}
              aria-describedby="change-note-hint"
            />
            <span
              id="change-note-hint"
              className={`text-xs ${canEdit ? "text-muted-foreground" : "text-amber-700"}`}
            >
              {canEdit
                ? `Saved against every ${MEASURE_LABEL[measure].toLowerCase()} edit`
                : "Add a note before editing; it is recorded with each change"}
            </span>
          </div>
        )}

        {/* Seed control */}
        {isDraft && (
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-3 text-sm">
            <Sparkles className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              Seed {MEASURE_LABEL[measure].toLowerCase()} from forecast:
            </span>
            <span>Most Likely scenario</span>
            <span className="text-muted-foreground">Stretch</span>
            <Input
              value={stretchPct}
              onChange={(e) => setStretchPct(e.target.value)}
              className="h-7 w-16 text-right tabular-nums"
              aria-label="Stretch percentage"
            />
            <span>%</span>
            <Button size="sm" variant="outline" onClick={requestSeed}>
              Apply (overwrites cells)
            </Button>
          </div>
        )}

        {/* Seed confirm (inline; no window.confirm) */}
        {seedConfirmOpen && isDraft && (
          <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
            <div className="font-medium">
              Replace this draft with the forecast seed?
            </div>
            <p className="text-muted-foreground">
              This overwrites{" "}
              <span className="font-medium">
                every {MEASURE_LABEL[measure].toLowerCase()} monthly cell
              </span>{" "}
              and{" "}
              <span className="font-medium">every manual FY-rep override</span>{" "}
              in that measure with forecast ×{" "}
              {(1 + (parseFloat(stretchPct) || 0) / 100).toFixed(2)}. Any nudges
              you've made by hand will be lost. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSeedConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={seedFromForecast}>
                Yes, overwrite
              </Button>
            </div>
          </div>
        )}

        {/* Delete confirm (inline; no window.confirm) */}
        {deleteConfirmOpen && isDraft && (
          <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
            <div className="font-medium">Delete this draft?</div>
            <p className="text-muted-foreground">
              All edits in this draft will be permanently lost. Any previously
              published version stays untouched.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={deleteDraft}
                className="bg-red-600 hover:bg-red-700"
              >
                Yes, delete draft
              </Button>
            </div>
          </div>
        )}

        {/* Publish dialog (inline) */}
        {publishOpen && isDraft && (
          <div className="mt-4 pt-4 border-t border-border space-y-2">
            <label className="text-sm font-medium" htmlFor="publish-notes">
              Publish notes (optional)
            </label>
            <Input
              id="publish-notes"
              value={publishNotes}
              onChange={(e) => setPublishNotes(e.target.value)}
              placeholder="e.g. FY26 board-approved targets, stretch +10%"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPublishOpen(false)}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={publish}>
                Publish v{selectedPlan?.version ?? 0}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Grid */}
      {loading ? (
        <Card className="p-4 space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </Card>
      ) : !grid ? (
        <Card className="p-8 text-center text-muted-foreground">
          {canManage
            ? `No plan loaded for FY${String(fiscalYear).slice(-2)}. Create a draft above to start.`
            : `No published targets for FY${String(fiscalYear).slice(-2)} yet.`}
        </Card>
      ) : (
        <>
          {historyOpen && (
            <ChangeHistory
              changes={visibleChanges}
              measure={measure}
              onClose={() => setHistoryOpen(false)}
            />
          )}
          <TargetGrid
            grid={grid}
            isDraft={isDraft}
            editable={canEdit}
            measureLabel={MEASURE_LABEL[measure]}
            onUpdateCell={updateCell}
            onUpdateRepFy={updateRepFy}
            onUpdateCompanyMonth={updateCompanyMonth}
            onUpdateCompanyFy={updateCompanyFy}
            onUpdateNewBizTarget={updateNewBizTarget}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Measure toggle + change history
// ---------------------------------------------------------------------------

function MeasureToggle({
  value,
  onChange,
}: {
  value: API.TargetMeasure;
  onChange: (m: API.TargetMeasure) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Target measure"
      className="inline-flex rounded-md border border-border overflow-hidden text-sm"
    >
      {(Object.keys(MEASURE_LABEL) as API.TargetMeasure[]).map((m) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={value === m}
          onClick={() => onChange(m)}
          className={`px-3 py-1.5 ${
            value === m
              ? "bg-primary/10 text-primary font-semibold"
              : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
          }`}
        >
          {MEASURE_LABEL[m]}
        </button>
      ))}
    </div>
  );
}

function ChangeHistory({
  changes,
  measure,
  onClose,
}: {
  changes: API.TargetPlanChange[];
  measure: API.TargetMeasure;
  onClose: () => void;
}) {
  return (
    <Card className="p-4 space-y-2" aria-label="Change history">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">
          {MEASURE_LABEL[measure]} change history
        </h2>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
      {changes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No {MEASURE_LABEL[measure].toLowerCase()} changes recorded on this
          plan.
        </p>
      ) : (
        <div className="overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="text-left py-1 pr-3 font-medium">When</th>
                <th className="text-left py-1 pr-3 font-medium">Who</th>
                <th className="text-left py-1 pr-3 font-medium">What</th>
                <th className="text-right py-1 pr-3 font-medium">Before</th>
                <th className="text-right py-1 pr-3 font-medium">After</th>
                <th className="text-left py-1 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c) => (
                <tr key={c.id} className="border-b border-border/50">
                  <td className="py-1 pr-3 whitespace-nowrap tabular-nums">
                    {formatDateTimeOrDash(c.changed_at)}
                  </td>
                  <td className="py-1 pr-3 whitespace-nowrap">
                    {c.changed_by_name ?? c.changed_by}
                  </td>
                  <td className="py-1 pr-3">
                    {CHANGE_SCOPE_LABEL[c.scope]}
                    {c.user_id ? ` · ${c.user_name ?? c.user_id}` : ""}
                    {c.year && c.month
                      ? ` · ${monthLabel(c.year, c.month)} ${c.year}`
                      : ""}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {c.before_amt === null ? "—" : fmtFy(c.before_amt)}
                  </td>
                  <td className="py-1 pr-3 text-right tabular-nums">
                    {fmtFy(c.after_amt)}
                  </td>
                  <td className="py-1">{c.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

interface TargetGridProps {
  grid: API.TargetPlanGrid;
  isDraft: boolean;
  /** Draft plus a change note in place: cells accept input. */
  editable: boolean;
  measureLabel: string;
  onUpdateCell: (
    userId: string,
    year: number,
    month: number,
    value: number,
  ) => void;
  onUpdateRepFy: (userId: string, value: number) => void;
  onUpdateCompanyMonth: (year: number, month: number, value: number) => void;
  onUpdateCompanyFy: (value: number) => void;
  onUpdateNewBizTarget: (
    userId: string,
    year: number,
    month: number,
    value: number,
  ) => void;
}

function TargetGrid({
  grid,
  isDraft,
  editable,
  measureLabel,
  onUpdateCell,
  onUpdateRepFy,
  onUpdateCompanyMonth,
  onUpdateCompanyFy,
  onUpdateNewBizTarget,
}: TargetGridProps) {
  const { months, reps, company, closed_months } = grid;
  const readOnly = !isDraft || !editable;

  return (
    <Card className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="text-left px-3 py-2 text-xs text-muted-foreground">
          {measureLabel} targets, £ per month
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="text-left px-3 py-2 font-semibold text-muted-foreground sticky left-0 bg-muted/30 min-w-[200px] z-10">
              Rep
            </th>
            <th className="text-right px-2 py-2 font-semibold text-muted-foreground min-w-[80px] border-l border-border">
              FY Tgt
            </th>
            <th className="text-right px-2 py-2 font-semibold text-muted-foreground min-w-[80px]">
              FY Fcst
            </th>
            {months.map((m, i) => (
              <th
                key={`${m.year}-${m.month}`}
                className={`text-center px-1 py-2 font-semibold min-w-[64px] ${
                  i < closed_months
                    ? "text-muted-foreground/60"
                    : "text-muted-foreground"
                }`}
              >
                {monthLabel(m.year, m.month)}
                {i < closed_months && (
                  <Lock className="inline-block w-3 h-3 ml-0.5 opacity-60" />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reps.map((rep) => {
            const cellSum = rep.monthly_targets.reduce((s, t) => s + t, 0);
            const fyGap = (rep.fy_target ?? 0) - cellSum;
            return (
              <RepBlock
                key={rep.user_id}
                rep={rep}
                months={months}
                closedMonths={closed_months}
                readOnly={readOnly}
                cellSum={cellSum}
                fyGap={fyGap}
                onUpdateCell={onUpdateCell}
                onUpdateRepFy={onUpdateRepFy}
                onUpdateNewBizTarget={onUpdateNewBizTarget}
              />
            );
          })}
          {/* Company totals row */}
          <CompanyBlock
            company={company}
            months={months}
            closedMonths={closed_months}
            readOnly={readOnly}
            onUpdateCompanyMonth={onUpdateCompanyMonth}
            onUpdateCompanyFy={onUpdateCompanyFy}
          />
        </tbody>
      </table>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Rep block (main row + new-business sub-row)
// ---------------------------------------------------------------------------

interface RepBlockProps {
  rep: API.TargetPlanRepRow;
  months: API.TargetPlanMonthTuple[];
  closedMonths: number;
  readOnly: boolean;
  cellSum: number;
  fyGap: number;
  onUpdateCell: (
    userId: string,
    year: number,
    month: number,
    value: number,
  ) => void;
  onUpdateRepFy: (userId: string, value: number) => void;
  onUpdateNewBizTarget: (
    userId: string,
    year: number,
    month: number,
    value: number,
  ) => void;
}

function RepBlock({
  rep,
  months,
  closedMonths,
  readOnly,
  cellSum,
  fyGap,
  onUpdateCell,
  onUpdateRepFy,
  onUpdateNewBizTarget,
}: RepBlockProps) {
  return (
    <>
      <tr className="border-t-2 border-border">
        <td className="px-3 py-2 font-medium sticky left-0 bg-card z-10">
          {rep.user_name}
        </td>
        <td className="px-1 py-1 border-l border-border">
          <FyOverrideCell
            value={rep.fy_target ?? 0}
            gap={fyGap}
            cellSum={cellSum}
            readOnly={readOnly}
            onCommit={(v) => onUpdateRepFy(rep.user_id, v)}
          />
        </td>
        <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
          {fmtFy(rep.fy_forecast)}
        </td>
        {months.map((m, i) => (
          <td key={`${m.year}-${m.month}`} className="align-top">
            <TargetCell
              value={rep.monthly_targets[i] ?? 0}
              ghost={rep.monthly_forecast[i] ?? 0}
              locked={i < closedMonths}
              readOnly={readOnly}
              onCommit={(v) => onUpdateCell(rep.user_id, m.year, m.month, v)}
            />
          </td>
        ))}
      </tr>
      {/* New business sub-row */}
      <tr className="bg-muted/10">
        <td className="px-3 py-1 text-xs text-muted-foreground italic sticky left-0 bg-muted/10 z-10">
          ↳ New business
        </td>
        <td className="px-2 py-1 text-right tabular-nums text-xs text-muted-foreground border-l border-border">
          {fmtFy(rep.monthly_new_business_target.reduce((s, v) => s + v, 0))}
        </td>
        <td className="px-2 py-1 text-right tabular-nums text-xs text-muted-foreground">
          {fmtFy(rep.monthly_new_business_forecast.reduce((s, v) => s + v, 0))}
        </td>
        {months.map((m, i) => (
          <td key={`${m.year}-${m.month}`} className="align-top">
            <TargetCell
              value={rep.monthly_new_business_target[i] ?? 0}
              ghost={rep.monthly_new_business_forecast[i] ?? 0}
              locked={i < closedMonths}
              readOnly={readOnly}
              onCommit={(v) =>
                onUpdateNewBizTarget(rep.user_id, m.year, m.month, v)
              }
            />
          </td>
        ))}
      </tr>
    </>
  );
}

// ---------------------------------------------------------------------------
// Company block
// ---------------------------------------------------------------------------

interface CompanyBlockProps {
  company: API.TargetPlanCompanyRow;
  months: API.TargetPlanMonthTuple[];
  closedMonths: number;
  readOnly: boolean;
  onUpdateCompanyMonth: (year: number, month: number, value: number) => void;
  onUpdateCompanyFy: (value: number) => void;
}

function CompanyBlock({
  company,
  months,
  closedMonths,
  readOnly,
  onUpdateCompanyMonth,
  onUpdateCompanyFy,
}: CompanyBlockProps) {
  const cellSum: number = company.monthly_targets.reduce<number>(
    (s, t) => s + (t ?? 0),
    0,
  );
  const fyGap = (company.fy_target ?? 0) - cellSum;
  return (
    <tr className="border-t-2 border-border bg-muted/20 font-semibold">
      <td className="px-3 py-2 sticky left-0 bg-muted/20 z-10">Company</td>
      <td className="px-1 py-1 border-l border-border">
        <FyOverrideCell
          value={company.fy_target ?? 0}
          gap={fyGap}
          cellSum={cellSum}
          readOnly={readOnly}
          onCommit={onUpdateCompanyFy}
        />
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
        {fmtFy(company.fy_forecast)}
      </td>
      {months.map((m, i) => (
        <td key={`${m.year}-${m.month}`} className="align-top">
          <TargetCell
            value={company.monthly_targets[i] ?? 0}
            ghost={company.monthly_forecast[i] ?? 0}
            locked={i < closedMonths}
            readOnly={readOnly}
            onCommit={(v) => onUpdateCompanyMonth(m.year, m.month, v)}
          />
        </td>
      ))}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// FY-override cell with gap indicator
// ---------------------------------------------------------------------------

interface FyOverrideCellProps {
  value: number;
  gap: number;
  cellSum: number;
  readOnly: boolean;
  onCommit: (v: number) => void;
}

function FyOverrideCell({
  value,
  gap,
  cellSum,
  readOnly,
  onCommit,
}: FyOverrideCellProps) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState(
    value > 0 ? `${Math.round(value / 1000)}` : "",
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!editing) {
      setRaw(value > 0 ? `${Math.round(value / 1000)}` : "");
    }
  }, [value, editing]);

  const showGap = value > 0 && gap !== 0;
  return (
    <div className="px-0.5 py-0.5">
      <Input
        value={raw}
        readOnly={readOnly}
        onFocus={() => setEditing(true)}
        onChange={(e) => {
          setRaw(e.target.value);
          setError(false);
        }}
        onBlur={() => {
          setEditing(false);
          const parsed = parseAmount(raw);
          if (parsed === null) {
            setError(true);
            return;
          }
          const next =
            raw.toLowerCase().includes("k") ||
            raw.toLowerCase().includes("m") ||
            raw.includes("£")
              ? parsed
              : parsed * 1000;
          if (next !== value) onCommit(next);
        }}
        className={`h-7 text-right text-sm font-semibold tabular-nums px-1 ${
          error ? "border-red-500 bg-red-50" : ""
        } ${readOnly ? "cursor-not-allowed opacity-60" : ""}`}
        placeholder="·"
        aria-invalid={error}
        title={`Cells sum: ${fmtFy(cellSum)}${showGap ? ` · Gap vs override: ${fmtFy(gap)}` : ""}`}
      />
      {showGap && (
        <div
          className={`text-center text-[10px] tabular-nums leading-tight ${
            gap > 0 ? "text-amber-700" : "text-blue-700"
          }`}
        >
          {gap > 0 ? "+" : ""}
          {fmtK(gap)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Save indicator
// ---------------------------------------------------------------------------

function SaveIndicator({
  state,
}: {
  state: "idle" | "saving" | "saved" | "error";
}) {
  if (state === "idle") return null;
  const colour =
    state === "saving"
      ? "text-muted-foreground"
      : state === "saved"
        ? "text-emerald-700"
        : "text-red-700";
  const label =
    state === "saving"
      ? "Saving…"
      : state === "saved"
        ? "Saved"
        : "Save failed";
  return (
    <span className={`text-xs ${colour} inline-flex items-center gap-1`}>
      {state === "saved" && <Check className="w-3 h-3" />}
      {label}
    </span>
  );
}
